import React, { useMemo, useCallback, useState, useEffect } from "react"
import { Formik, Form as FormikForm } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import { Label, Button, FormFeedback, Row, Col } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import StatusButtonGroup from "components/Common/StatusButtonGroup"
import VehicleIdSelect from "components/Common/VehicleIdSelect"

import {
  useCreateAccessoryUseRequest,
  useEditAccessoryUseRequest,
} from "hooks/useAccessoryUseRequests"
import { useAccessoriesListsWithStock } from "hooks/useAccessoriesLists"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"

const emptyItem = { accessory_id: "", unit_id: "", quantity: 1, remarks: "" }

const AccessoryUseRequestAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
  onSuccess,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccessoryUseRequest()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccessoryUseRequest()

  // -------------------- Dropdown options --------------------
  const { raw: rawOptions, isLoading: optionLoading } =
    useFilteredVehicleOptions()
  const { data: accessoriesWithStockData } = useAccessoriesListsWithStock({
    all: true,
  })

  const [createdAccessories, setCreatedAccessories] = useState([])
  const [createdUnits, setCreatedUnits] = useState([])

  useEffect(() => {
    if (!modal) {
      setCreatedAccessories([])
      setCreatedUnits([])
    }
  }, [modal])

  const accessoriesWithStock = useMemo(
    () => accessoriesWithStockData?.data ?? [],
    [accessoriesWithStockData]
  )

  const employeeOptions = useMemo(
    () =>
      (rawOptions?.employees ?? []).map(e => ({
        value: e.id?.toString(),
        label: e.name,
      })),
    [rawOptions?.employees]
  )

  const accessoryOptions = useMemo(() => {
    const dbAccessories = accessoriesWithStock.map(a => ({
      value: a.id?.toString(),
      label: a.name,
      available_stock: a.available_stock ?? 0,
      unit_name: a.unit?.name || "",
    }))
    const created = createdAccessories.map(a => ({
      value: a.id,
      label: a.name,
      available_stock: null,
      unit_name: "",
    }))
    return [...dbAccessories, ...created]
  }, [accessoriesWithStock, createdAccessories])

  const dbAccessoryIds = useMemo(
    () => new Set(accessoriesWithStock.map(a => String(a.id))),
    [accessoriesWithStock]
  )

  const isNewAccessory = useCallback(
    accessoryId => !!accessoryId && !dbAccessoryIds.has(String(accessoryId)),
    [dbAccessoryIds]
  )

  const unitOptions = useMemo(() => {
    const dbUnits = (rawOptions?.units ?? []).map(u => ({
      value: u.id?.toString(),
      label: u.name,
    }))
    const created = createdUnits.map(u => ({ value: u.id, label: u.name }))
    return [...dbUnits, ...created]
  }, [rawOptions?.units, createdUnits])

  const getAvailableStock = useCallback(
    accessoryId => {
      const option = accessoryOptions.find(
        o => String(o.value) === String(accessoryId)
      )
      return option?.available_stock ?? null
    },
    [accessoryOptions]
  )

  // -------------------- Form initial values --------------------
  const initialValues = useMemo(() => {
    const initialItems = editedData?.items?.length
      ? editedData.items.map(item => ({
          id: item.id,
          accessory_id: item.accessory_id?.toString() || "",
          unit_id: "",
          quantity: item.quantity || 1,
          remarks: item.remarks || "",
        }))
      : [{ ...emptyItem }]

    let vehicleValue = ""
    if (editedData?.vehicle?.id) {
      vehicleValue = {
        label:
          editedData.vehicle.stock_id +
          " - " +
          (editedData.vehicle.full_chassis || ""),
        value: editedData.vehicle.id,
      }
    }

    return {
      request_date: editedData?.request_date
        ? editedData.request_date.slice(0, 16)
        : "",
      requested_by_id: editedData?.requested_by_id?.toString() || "",
      vehicle_id: vehicleValue,
      is_self_vehicle:
        editedData?.is_self_vehicle !== undefined
          ? editedData.is_self_vehicle
          : true,
      notes: editedData?.notes || "",
      items: initialItems,
    }
  }, [editedData])

  // -------------------- Validation --------------------
  const validationSchema = useMemo(
    () =>
      Yup.object({
        request_date: Yup.date().required("Please select the request date"),
        vehicle_id: Yup.mixed().required("Please select a vehicle"),
        items: Yup.array()
          .of(
            Yup.object({
              accessory_id: Yup.string().required("Select an accessory"),
              unit_id: Yup.string().when("accessory_id", (accessory_id, schema) => {
                const idStr = String(accessory_id ?? "")
                if (!idStr) return schema
                return dbAccessoryIds.has(idStr)
                  ? schema
                  : schema.required("Select a unit")
              }),
              quantity: Yup.number()
                .required("Enter quantity")
                .moreThan(0, "Quantity must be greater than 0"),
            })
          )
          .min(1, "Add at least one accessory"),
      }),
    [dbAccessoryIds]
  )

  // -------------------- Option creation --------------------
  const handleCreateAccessory = useCallback(inputValue => {
    const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCreatedAccessories(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  const handleCreateUnit = useCallback(inputValue => {
    const tempId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setCreatedUnits(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  // -------------------- Submit --------------------
  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const resolveCreated = (id, createdList) => {
          if (id && String(id).startsWith("new_")) {
            return createdList.find(c => c.id === id)?.name ?? id
          }
          return id
        }

        const payload = {
          request_date: values.request_date,
          requested_by_id: values.requested_by_id || null,
          vehicle_id: values.vehicle_id?.value || values.vehicle_id,
          is_self_vehicle: values.is_self_vehicle !== false,
          notes: values.notes || null,
          items: values.items.map(item => ({
            id: item.id,
            accessory_id: resolveCreated(item.accessory_id, createdAccessories),
            unit_id: resolveCreated(item.unit_id, createdUnits) || null,
            quantity: Number(item.quantity),
            remarks: item.remarks || null,
          })),
        }

        const res = isEdit
          ? await editMutation({ id: editedData.id, ...payload })
          : await createMutation(payload)

        toast.success(
          res?.message ??
            `Accessory use request ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
        onSuccess?.()
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to save accessory use request."
        )
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess, createdAccessories, createdUnits]
  )

  const submitLoading = createLoading || editLoading

  // -------------------- Render --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Accessory Use Request" : "Add Accessory Use Request"}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {formik => {
          const addItem = () =>
            formik.setFieldValue("items", [
              ...formik.values.items,
              { ...emptyItem },
            ])
          const removeItem = index => {
            const newItems = formik.values.items.filter((_, i) => i !== index)
            formik.setFieldValue(
              "items",
              newItems.length ? newItems : [{ ...emptyItem }]
            )
          }

          return (
            <FormikForm>
              {/* Request Date */}
              <div className="mb-3">
                <Label htmlFor="request_date">
                  Request Date <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="datetime-local"
                  name="request_date"
                  id="request_date"
                  value={formik.values.request_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.request_date && !!formik.errors.request_date
                  }
                />
                {formik.touched.request_date && formik.errors.request_date && (
                  <FormFeedback type="invalid">
                    {formik.errors.request_date}
                  </FormFeedback>
                )}
              </div>

              {/* Requested By */}
              <FormikCreatableSelect
                name="requested_by_id"
                label="Requested By"
                formik={formik}
                options={employeeOptions}
                placeholder="Select employee"
                loading={optionLoading}
              />

              <StatusButtonGroup
                name="is_self_vehicle"
                label="Vehicle type"
                value={formik.values.is_self_vehicle}
                onChange={value =>
                  formik.setFieldValue("is_self_vehicle", value)
                }
                options={[
                  { value: true, label: "Self vehicle", color: "primary" },
                  { value: false, label: "Not self vehicle", color: "secondary" },
                ]}
              />

              {/* Vehicle */}
              <VehicleIdSelect formik={formik} />

              {/* Accessories */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0 fw-semibold">
                    Accessories <span className="text-danger">*</span>
                    <span className="badge bg-light text-muted ms-2">
                      {formik.values.items.length}
                    </span>
                  </Label>
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    type="button"
                    onClick={addItem}
                  >
                    <i className="mdi mdi-plus me-1" />
                    Add Accessory
                  </Button>
                </div>

                {typeof formik.errors.items === "string" && (
                  <div className="text-danger small mb-2">
                    {formik.errors.items}
                  </div>
                )}

                <div className="border rounded">
                  {formik.values.items.map((item, index) => {
                    const availableStock = getAvailableStock(item.accessory_id)
                    const selectedOption = accessoryOptions.find(
                      o => String(o.value) === String(item.accessory_id)
                    )
                    const accessoryIsNew = isNewAccessory(item.accessory_id)

                    return (
                      <div
                        key={index}
                        className={`p-3 ${index > 0 ? "border-top" : ""}`}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary-subtle text-primary">
                              #{index + 1}
                            </span>
                            {item.accessory_id && selectedOption && (
                              <span className="badge bg-light text-muted">
                                Stock:{" "}
                                {availableStock === null
                                  ? "N/A (new)"
                                  : `${availableStock} ${selectedOption.unit_name}`.trim()}
                              </span>
                            )}
                          </div>
                          <Button
                            color="link"
                            size="sm"
                            type="button"
                            className="text-danger p-0 lh-1"
                            onClick={() => removeItem(index)}
                            title="Remove accessory"
                          >
                            <i className="mdi mdi-delete-outline fs-5" />
                          </Button>
                        </div>

                        <Row className="g-2">
                          <Col md={6}>
                            <FormikCreatableSelect
                              name={`items.${index}.accessory_id`}
                              label="Accessory"
                              formik={formik}
                              options={accessoryOptions}
                              placeholder="Select accessory"
                              loading={optionLoading}
                              required
                              onCreateOption={handleCreateAccessory}
                              onChange={() => {
                                formik.setFieldValue(`items.${index}.unit_id`, "")
                              }}
                            />
                          </Col>

                          {item.accessory_id && (
                            <Col md={6}>
                              {accessoryIsNew ? (
                                <FormikCreatableSelect
                                  name={`items.${index}.unit_id`}
                                  label="Unit"
                                  formik={formik}
                                  options={unitOptions}
                                  placeholder="Select or type a unit"
                                  loading={optionLoading}
                                  required
                                  onCreateOption={handleCreateUnit}
                                />
                              ) : (
                                <>
                                  <Label>Unit</Label>
                                  <InputField
                                    type="text"
                                    value={selectedOption?.unit_name || "—"}
                                    disabled
                                  />
                                </>
                              )}
                            </Col>
                          )}

                          <Col md={6}>
                            <Label>
                              Quantity <span className="text-danger">*</span>
                            </Label>
                            <InputField
                              type="number"
                              name={`items.${index}.quantity`}
                              value={item.quantity}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              min="1"
                              invalid={
                                formik.touched.items?.[index]?.quantity &&
                                !!formik.errors.items?.[index]?.quantity
                              }
                            />
                            {formik.touched.items?.[index]?.quantity &&
                              formik.errors.items?.[index]?.quantity && (
                                <FormFeedback type="invalid" className="d-block">
                                  {formik.errors.items[index].quantity}
                                </FormFeedback>
                              )}
                          </Col>

                          <Col md={6}>
                            <Label>Remarks</Label>
                            <InputField
                              type="text"
                              name={`items.${index}.remarks`}
                              value={item.remarks}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Optional"
                            />
                          </Col>
                        </Row>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Notes */}
              <div className="mb-3">
                <Label htmlFor="notes">Notes</Label>
                <InputField
                  type="textarea"
                  name="notes"
                  id="notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              {/* Submit */}
              <div className="text-end mt-3">
                <Button color="success" type="submit" disabled={submitLoading}>
                  {isEdit ? "Save Changes" : "Add Request"}
                  {submitLoading ? "..." : ""}
                </Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>
    </ThemeModal>
  )
}

export default AccessoryUseRequestAddEdit
